// LandingPage.js
import React, { useState } from 'react';
import Header from '../components/Header';
import styles from './LandingPage.module.css';
import ContactFooter from '../components/ContactFooter';

// Organized technical expertise by domain
const expertiseData = {
    'AI & Machine Learning': [
        { 
            name: 'Deep Learning Research', 
            category: 'ai', 
            level: 'Expert',
            relatedWorks: [
                { type: 'blog', title: 'DeepSeek R1 Weight Analysis', url: '/technology/deepseek_R1_weight_analysis' },
                { type: 'repo', title: 'MLA Attention Experiment', url: '/repo-entry/mla_attention_experiment.git' },
            ]
        },
        { 
            name: 'Computer Vision', 
            category: 'ai', 
            level: 'Expert',
            relatedWorks: [
                { type: 'blog', title: 'Autoencoder Convolutional', url: '/technology/autoencoder_conv' },
                { type: 'blog', title: 'Autoencoder Transformer', url: '/technology/autoencoder_trans' },
                { type: 'repo', title: 'Data Matrix', url: '/repo-entry/data_matrix.git' },
            ]
        },
        { 
            name: 'CUDA Programming', 
            category: 'ai', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'CUDA Benchmark', url: '/repo-entry/cuda_suite.git/benchmark' },
                { type: 'repo', title: 'CUDA Kernel', url: '/repo-entry/cuda_suite.git/cuda_kernel' },
                { type: 'repo', title: 'CUDA Optimization Suite', url: '/repo-entry/cuda_suite.git' },
            ]
        },
        { 
            name: 'Neural Architecture Design', 
            category: 'ai', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'Autoencoder Research', url: '/repo-entry/autoencoder_research.git' },
                { type: 'repo', title: 'Data Matrix', url: '/repo-entry/data_matrix.git' },
            ]
        },
    ],
    'Systems & Hardware': [
        { 
            name: 'Embedded Systems', 
            category: 'hardware', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'Radio Analysis Toolkit', url: '/repo-entry/radio_toolkit.git' }, 
                { type: 'blog', title: 'Building a Custom Linux Kernel', url: '/technology/linux_kernel' },
            ]
        },
        { 
            name: 'Electronics Design', 
            category: 'hardware', 
            level: 'Expert',
            relatedWorks: []
        },
        { 
            name: 'Optical Systems', 
            category: 'hardware', 
            level: 'Expert',
            relatedWorks: []
        },
        { 
            name: 'Linux Kernel', 
            category: 'hardware', 
            level: 'Advanced',
            relatedWorks: [
                { type: 'blog', title: 'Building a Custom Linux Kernel', url: '/technology/linux_kernel' },
                { type: 'repo', title: 'Compiling Linux From Scratch', url: '/repo-entry/linux_compile.git' }
            ]
        },
    ],
    'Programming': [
        { 
            name: 'C/C++', 
            category: 'languages', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'Leetcode', url: '/repo-entry/leetcode.git' },
                { type: 'repo', title: 'LLM Engine', url: '/repo-entry/llm_engine.git' },
                { type: 'repo', title: 'CUDA Optimization Suite', url: '/repo-entry/cuda_suite.git' },
                { type: 'repo', title: 'Flutter Client', url: '/repo-entry/flutter_client.git' },
                { type: 'repo', title: 'Serial Terminal', url: '/repo-entry/serial_terminal.git' },
            ]
        },
        
        { 
            name: 'Rust', 
            category: 'languages', 
            level: 'Advanced',
            relatedWorks: [
                { type: 'repo', title: 'Leetcode', url: '/repo-entry/leetcode.git' },
                { type: 'repo', title: 'Boxsim', url: '/repo-entry/boxsim.git' },
                { type: 'repo', title: 'Edge Server', url: '/repo-entry/edge_server.git' },
                { type: 'repo', title: 'System Monitor', url: '/repo-entry/system_monitor.git' },
            ]
        },
        { 
            name: 'Python', 
            category: 'languages', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'Autoencoder Research', url: '/repo-entry/autoencoder_research.git' },
            ]
        },
        { 
            name: 'JavaScript', 
            category: 'languages', 
            level: 'Advanced',
            relatedWorks: [
                { type: 'repo', title: 'Entrosystem', url: '/repo-entry/entrosystem.git' },
            ]
        },
        { 
            name: 'GPU Assembly (PTX, SASS)', 
            category: 'languages', 
            level: 'Advanced',
            relatedWorks: [
                { type: 'repo', title: 'PTX and SASS Optimization', url: '/repo-entry/cuda_suite.git/experiment' },
                { type: 'repo', title: 'PTX and SASS dissection', url: '/repo-entry/cuda_suite.git/benchmark' },
            ]
        },
        { 
            name: 'CPU Assembly, SIMD Instruction', 
            category: 'languages', 
            level: 'Advanced',
            relatedWorks: [
                { type: 'repo', title: 'Cuda Suite', url: '/repo-entry/cuda_suite.git/experiment'},

            ]
        },
    ],
    'Scientific Computing': [
        { 
            name: 'Mathematical Modeling', 
            category: 'science', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'CUDA Computing', url: '/repo-entry/cuda_suite.git' },
            ]
        },
        { 
            name: 'Physics Simulation', 
            category: 'science', 
            level: 'Expert',
            relatedWorks: []
        },
        { 
            name: 'Data Analysis', 
            category: 'science', 
            level: 'Expert',
            relatedWorks: [
                { type: 'repo', title: 'Radio Analysis Toolkit', url: '/repo-entry/radio_toolkit.git' }, 
            ]
        },
        { 
            name: 'Statistical Methods', 
            category: 'science', 
            level: 'Advanced',
            relatedWorks: []
        },
    ]
};

// Research focus areas
const researchAreas = [
    {
        title: "Multimodal AI Systems",
        description: "Developing next-generation AI architectures that seamlessly integrate visual, textual, and sensory data streams.",
        relatedContent: [
            { type: 'blog', title: 'Autoencoder Convolutional', url: '/technology/autoencoder_conv' },
            { type: 'blog', title: 'Autoencoder Transformer', url: '/technology/autoencoder_trans' },
            { type: 'repo', title: 'Autoencoder Research', url: '/repo-entry/autoencoder_research.git' },
            { type: 'blog', title: 'Mixture of Experts', url: '/technology/moe' },
            { type: 'blog', title: 'Deepseekv2 Dissect', url: '/technology/deepseekv2-dissect' },
            { type: 'blog', title: 'Transformer', url: '/technology/transformer' },
            { type: 'blog', title: 'GAN', url: '/technology/gan' },
            { type: 'blog', title: 'DQN', url: '/technology/dqn' },
            { type: 'blog', title: 'Image-diffusion', url: '/technology/image-diffusion' },
            { type: 'repo', title: 'Model Dissection', url: '/repo-entry/model_dissect.git' },
            { type: 'repo', title: 'Data Matrix', url: '/repo-entry/data_matrix.git' },
        ]
    },
    {
        title: "Hardware-Software Co-Design",
        description: "Optimizing AI inference pipelines through low level hardware optimization (SASS, PTX, and SIMD) and algorithmic innovations.",
        relatedContent: [
            { type: 'repo', title: 'LLM Engine', url: '/repo-entry/llm_engine.git' },
            { type: 'repo', title: 'CUDA Benchmark', url: '/repo-entry/cuda_suite.git/benchmark' },
            { type: 'repo', title: 'CUDA Kernel', url: '/repo-entry/cuda_suite.git/cuda_kernel' },
            { type: 'repo', title: 'CUDA Optimization Suite', url: '/repo-entry/cuda_suite.git' },
            { type: 'repo', title: 'Leetcode', url: '/repo-entry/leetcode.git' },
            { type: 'blog', title: 'Humanoid Robot', url: '/technology/humanoid ' },
            { type: 'blog', title: 'Building a Custom Linux Kernel', url: '/technology/linux_kernel' },
        ]
    },
    {
        title: "Computational Science",
        description: "Understanding the behavior of complex systems through mathematical modeling and simulation.",
        relatedContent: [
            { type: 'repo', title: 'CUDA Computing', url: '/repo-entry/cuda_suite.git' },
            { type: 'repo', title: 'Radio Analysis Toolkit', url: '/repo-entry/radio_toolkit.git' },            
        ]
    }
];


export default function LandingPage() {
    const pageSubtitle = "AI Engineer & Researcher | Bridging Physics & Artificial Intelligence";
    const bannerImageUrl = "/images/BannerPage1.jpg";
    const [expandedAreas, setExpandedAreas] = useState({});
    const [expandedSkills, setExpandedSkills] = useState({});

    return (
        <div className={styles.pageContainer}>
            <Header backgroundImageUrl={bannerImageUrl} subtitle={pageSubtitle} />
            
            <main>
                <section id="about" className={styles.section}>
                    <h2 className={styles.sectionTitle}>Professional Profile</h2>
                    <div className={styles.profileSection}>
                        <div className={styles.profileInfo}>
                            <div className={styles.profileImageContainer}>
                                <div
                                    className={styles.profileImage}
                                    style={{
                                        backgroundImage: "url(/images/profile_picture_round.jpg)",
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    }}
                                ></div>
                            </div>
                            <div className={styles.profileDetails}>
                                <h3 className={styles.profileName}>Gesit Tali Singgih</h3>
                                <a
                                    href="https://x.com/entropicEm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.socialLink}
                                    aria-label="Follow on X (Twitter)"
                                >
                                    <svg
                                        className={styles.socialIcon}
                                        viewBox="0 0 24 24"
                                        width="16"
                                        height="16"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                    <span className={styles.socialText}>@entropicEm</span>
                                </a>
                            </div>
                        </div>
                        <div className={styles.profileContent}>
                            <p className={styles.leadParagraph}>
                                AI Engineer and Researcher with expertise spanning physics,
                                high-precision optical systems, and cutting-edge artificial intelligence.
                            </p>
                            <p className={styles.paragraph}>
                                My multidisciplinary background bridges the gap between fundamental science and
                                practical AI implementation. With experience building complex systems from hardware
                                to high-level abstractions, I bring a unique perspective to solving challenging
                                problems in machine learning, computer vision, and intelligent systems design.
                            </p>
                            <p className={styles.paragraph}>
                                Currently focused on advanced multimodal AI architectures, hardware acceleration
                                techniques, and in depth neural network behavior.
                                I maintain active engagement with the latest research while delivering production-ready
                                solutions across diverse technical domains.
                            </p>
                        </div>
                    </div>
                </section>

                <section id="research" className={`${styles.section} ${styles.research}`}>
                    <h2 className={styles.sectionTitle}>Research Focus</h2>
                    <div className={styles.researchGrid}>
                        {researchAreas.map((area, index) => (
                            <div key={index} className={styles.researchCard}>
                                <h3 className={styles.researchTitle}>{area.title}</h3>
                                <p className={styles.researchDescription}>{area.description}</p>
                                
                                {area.relatedContent && area.relatedContent.length > 0 && (
                                    <div className={styles.relatedContentSection}>
                                        <button 
                                            className={styles.expandButton}
                                            onClick={() => setExpandedAreas(prev => ({
                                                ...prev,
                                                [index]: !prev[index]
                                            }))}
                                        >
                                            <span>Related Works</span>
                                            <span className={`${styles.expandIcon} ${expandedAreas[index] ? styles.expanded : ''}`}>
                                                ▼
                                            </span>
                                        </button>
                                        
                                        {expandedAreas[index] && (
                                            <div className={styles.relatedContentList}>
                                                {area.relatedContent.map((item, itemIndex) => (
                                                    <a 
                                                        key={itemIndex}
                                                        href={item.url}
                                                        className={`${styles.relatedItem} ${styles[item.type]}`}
                                                    >
                                                        <span className={styles.itemIcon}>
                                                            {item.type === 'blog' ? '📝' : '📁'}
                                                        </span>
                                                        <span className={styles.itemTitle}>{item.title}</span>
                                                        <span className={styles.itemType}>{item.type}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section id="expertise" className={`${styles.section} ${styles.expertise}`}>
                    <h2 className={styles.sectionTitle}>Technical Expertise</h2>
                    <div className={styles.expertiseContainer}>
                        {Object.entries(expertiseData).map(([category, skills]) => (
                            <div key={category} className={styles.expertiseCategory}>
                                <h3 className={styles.categoryTitle}>{category}</h3>
                                <div className={styles.skillsList}>
                                    {skills.map((skill, skillIndex) => {
                                        const skillKey = `${category}-${skillIndex}`;
                                        const hasRelatedWorks = skill.relatedWorks && skill.relatedWorks.length > 0;
                                        
                                        return (
                                            <div key={skillIndex} className={styles.skillItemWrapper}>
                                                <div 
                                                    className={`${styles.skillItem} ${hasRelatedWorks ? styles.hasRelatedWorks : ''}`}
                                                    onClick={() => hasRelatedWorks && setExpandedSkills(prev => ({
                                                        ...prev,
                                                        [skillKey]: !prev[skillKey]
                                                    }))}
                                                    style={{ cursor: hasRelatedWorks ? 'pointer' : 'default' }}
                                                >
                                                    <div className={styles.skillMainContent}>
                                                        <span className={styles.skillName}>{skill.name}</span>
                                                        <div className={styles.skillControls}>
                                                            {/* <span className={`${styles.skillLevel} ${styles[skill.level.toLowerCase()]}`}>
                                                                {skill.level}
                                                            </span> */}
                                                            {hasRelatedWorks && (
                                                                <span 
                                                                    className={styles.skillExpandIcon}
                                                                    style={{ 
                                                                        transform: expandedSkills[skillKey] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                        transition: 'transform 0.2s ease'
                                                                    }}
                                                                >
                                                                    ▼
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {hasRelatedWorks && expandedSkills[skillKey] && (
                                                    <div className={styles.skillRelatedWorks}>
                                                        {skill.relatedWorks.map((work, workIndex) => (
                                                            <a 
                                                                key={workIndex}
                                                                href={work.url}
                                                                className={`${styles.skillRelatedItem} ${styles[work.type]}`}   
                                                            >
                                                                <span className={styles.skillItemIcon}>
                                                                    {work.type === 'blog' ? '📝' : '📁'}
                                                                </span>
                                                                <span className={styles.skillItemTitle}>{work.title}</span>
                                                                <span className={styles.skillItemType}>{work.type}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/*
                <section id="achievements" className={`${styles.section} ${styles.achievements}`}>
                    <h2 className={styles.sectionTitle}>Key Achievements</h2>
                    <div className={styles.achievementsList}>
                        <div className={styles.achievement}>
                            <h4>Cross-Disciplinary Innovation</h4>
                            <p>Successfully transitioned from theoretical physics to AI research, bringing unique perspectives to neural network architectures and optimization techniques.</p>
                        </div>
                        <div className={styles.achievement}>
                            <h4>Full-Stack Technical Proficiency</h4>
                            <p>Demonstrated capability across the complete technology stack, from low-level hardware programming to high-level AI system design and deployment.</p>
                        </div>
                        <div className={styles.achievement}>
                            <h4>Research & Development Leadership</h4>
                            <p>Actively contributing to cutting-edge AI research while maintaining practical implementation experience in production environments.</p>
                        </div>
                    </div>
                </section>
                */}

                <section id="featured" className={`${styles.section} ${styles.featured}`}>
                    <h2 className={styles.sectionTitle}>Featured In</h2>
                    <div className={styles.featuredList}>
                        <div className={styles.featuredItem}>
                            <div className={styles.featuredContent}>
                                <h4>Medical Ventilator Development During COVID-19</h4>
                                <p>Work on building medical ventilators during the COVID-19 pandemic, demonstrating rapid prototyping and critical advanced healthcare technology development.</p>
                                <div className={styles.featuredVideos}>
                                    <a 
                                        href="https://www.youtube.com/watch?v=aVQfepUpeV4" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={styles.videoThumbnail}
                                    >
                                        <div className={styles.thumbnailContainer}>
                                            <img 
                                                src="https://img.youtube.com/vi/aVQfepUpeV4/maxresdefault.jpg" 
                                                alt="Medical Ventilator Development Video 1"
                                                className={styles.thumbnailImage}
                                            />
                                            <div className={styles.playOverlay}>
                                                <div className={styles.playButton}>▶</div>
                                            </div>
                                        </div>
                                        <div className={styles.videoTitle}>Medical Ventilator Project - Part 1</div>
                                    </a>
                                    <a 
                                        href="https://www.youtube.com/watch?v=iJATvMtsVAc" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={styles.videoThumbnail}
                                    >
                                        <div className={styles.thumbnailContainer}>
                                            <img 
                                                src="https://img.youtube.com/vi/iJATvMtsVAc/maxresdefault.jpg" 
                                                alt="Medical Ventilator Development Video 2"
                                                className={styles.thumbnailImage}
                                            />
                                            <div className={styles.playOverlay}>
                                                <div className={styles.playButton}>▶</div>
                                            </div>
                                        </div>
                                        <div className={styles.videoTitle}>Medical Ventilator Project - Part 2</div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                
            </main>

            <ContactFooter 
                title="Professional Collaboration"
                description="Available for consulting on AI research, system architecture, and technical leadership opportunities. Open to discussing innovative projects at the intersection of physics and artificial intelligence."
                copyrightText="© 2025 Entrosystem. All rights reserved."
                backgroundImageUrl={bannerImageUrl}
            />
        </div>
    );
}